import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {
	IDivisionSlot,
	ILightPredictionNew,
	ILightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {GameSteps, TutorialType} from "data/enums";
import {type ITutorialStore} from "data/stores/tutorial/tutorial.store";

export interface ILightPredictorPredictionsController extends ViewController {
	readonly i18n: ILocalizationStore;

	get currentStep(): GameSteps;
	get lightPrediction(): ILightPredictionNew;
	get nfcPredictions(): IDivisionSlot[];
	get afcPredictions(): IDivisionSlot[];
	get isChampionsSelect(): boolean;
	get isOrderStep(): boolean;
	get isLast3SquadsSelect(): boolean;
	get isPredictionsSaved(): boolean;

	openTutorialHandler: () => void;
}

@injectable()
export class LightPredictorPredictionsController implements ILightPredictorPredictionsController {
	get currentStep() {
		return this._lightPredictorStore.currentStep;
	}

	get lightPrediction() {
		return this._lightPredictorStore.lightPrediction;
	}

	get nfcPredictions() {
		return this._lightPredictorStore.nfcPredictions;
	}

	get afcPredictions() {
		return this._lightPredictorStore.afcPredictions;
	}

	get isChampionsSelect() {
		return [
			GameSteps.NFC_WEST,
			GameSteps.NFC_SOUTH,
			GameSteps.NFC_NORTH,
			GameSteps.NFC_EAST,
			GameSteps.AFC_WEST,
			GameSteps.AFC_SOUTH,
			GameSteps.AFC_NORTH,
			GameSteps.AFC_EAST,
		].includes(this.currentStep);
	}

	get isOrderStep() {
		return [GameSteps.NFC_SEEDING, GameSteps.AFC_SEEDING].includes(this.currentStep);
	}

	get isLast3SquadsSelect() {
		return [GameSteps.AFC_WILD_CARD, GameSteps.NFC_WILD_CARD].includes(this.currentStep);
	}

	get isPredictionsSaved() {
		return this._lightPredictorStore.isPredictionsSaved;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.LightPredictionsStore)
		private _lightPredictorStore: ILightPredictionsStore,
		@inject(Bindings.TutorialStore) private _tutorialStore: ITutorialStore
	) {
		makeAutoObservable(this);
	}

	openTutorialHandler = () => {
		this._tutorialStore.openTutorial(TutorialType.LIGHT_PREDICTOR);
	};
}
